import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

interface CommunityTablePaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const CommunityTablePagination: React.FC<CommunityTablePaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const [jumpPage, setJumpPage] = useState('');

  useEffect(() => {
    setJumpPage('');
  }, [currentPage, totalPages]);

  const effectiveTotalPages = Math.max(1, totalPages);

  const visiblePages = 9;
  let startPage = Math.max(1, currentPage - Math.floor(visiblePages / 2));
  let endPage = Math.min(effectiveTotalPages, startPage + visiblePages - 1);

  if (endPage - startPage + 1 < visiblePages) {
    startPage = Math.max(1, endPage - visiblePages + 1);
  }

  const handlePageJump = () => {
    if (jumpPage.trim() === '') return;
    const page = parseInt(jumpPage, 10);
    if (Number.isNaN(page) || page < 1 || page > effectiveTotalPages) {
      alert(`1 ~ ${effectiveTotalPages} 사이의 페이지 번호를 입력해 주세요.`);
      setJumpPage('');
      return;
    }
    onPageChange(page);
    setJumpPage('');
  };

  const pages = [];

  pages.push(
    <PaginationButton
      key="prev"
      type="button"
      onClick={() => onPageChange(currentPage - 1)}
      $disabled={currentPage === 1}
      disabled={currentPage === 1}
    >
      <PaginationButtonContent>
        <PaginationButtonInner>
          <ArrowIcon
            width="8"
            height="16"
            viewBox="0 0 8 16"
            fill="none"
            $disabled={currentPage === 1}
          >
            <path
              d="M6.5 1L1 8L6.5 15"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </ArrowIcon>
        </PaginationButtonInner>
      </PaginationButtonContent>
    </PaginationButton>
  );

  for (let page = startPage; page <= endPage; page += 1) {
    pages.push(
      <PaginationButton
        key={page}
        type="button"
        onClick={() => onPageChange(page)}
        $active={currentPage === page}
      >
        <PaginationButtonContent>
          <PaginationButtonInner>
            <PaginationButtonText $active={currentPage === page}>
              {page}
            </PaginationButtonText>
          </PaginationButtonInner>
        </PaginationButtonContent>
      </PaginationButton>
    );
  }

  pages.push(
    <PaginationButton
      key="next"
      type="button"
      onClick={() => onPageChange(currentPage + 1)}
      $disabled={currentPage === effectiveTotalPages}
      disabled={currentPage === effectiveTotalPages}
    >
      <PaginationButtonContent>
        <PaginationButtonInner>
          <ArrowIcon
            width="8"
            height="16"
            viewBox="0 0 8 16"
            fill="none"
            $disabled={currentPage === effectiveTotalPages}
            $right
          >
            <path
              d="M1.5 1L7 8L1.5 15"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </ArrowIcon>
        </PaginationButtonInner>
      </PaginationButtonContent>
    </PaginationButton>
  );

  return (
    <PaginationContainer>
      <PaginationWrapper>
        <Pagination>
          <PaginationInner>{pages}</PaginationInner>
        </Pagination>
        <PageJumpContainer>
          <PageJumpLabel>페이지 이동</PageJumpLabel>
          <PageJumpInputWrapper>
            <PageJumpInputContainer>
              <PageJumpInputContent>
                <PageJumpInput
                  type="number"
                  value={jumpPage}
                  onChange={(e) => setJumpPage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handlePageJump();
                    }
                  }}
                  placeholder={String(currentPage)}
                  min="1"
                  max={effectiveTotalPages}
                />
              </PageJumpInputContent>
            </PageJumpInputContainer>
          </PageJumpInputWrapper>
        </PageJumpContainer>
      </PaginationWrapper>
    </PaginationContainer>
  );
};

const PaginationContainer = styled.div`
  align-self: stretch;
  padding: 16px 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  border-top: 1px solid var(--Line-Normal-Neutral, rgba(112, 115, 124, 0.16));
`;

const PaginationWrapper = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Pagination = styled.div`
  min-height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
`;

const PaginationInner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
`;

const PaginationButton = styled.button<{ $active?: boolean; $disabled?: boolean }>`
  width: 20px;
  height: 30px;
  min-width: 20px;
  padding: 4px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 6px;
  overflow: hidden;

  &:hover:not(:disabled) {
    background: rgba(112, 115, 124, 0.05);
  }

  ${({ $active }) =>
    $active &&
    css`
      background: rgba(23, 23, 25, 0.09);
    `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      cursor: not-allowed;
      opacity: 0.5;
    `}
`;

const PaginationButtonContent = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PaginationButtonInner = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PaginationButtonText = styled.div<{ $active?: boolean }>`
  text-align: center;
  font-size: 15px;
  font-family: 'Pretendard Variable', sans-serif;
  line-height: 22.01px;
  letter-spacing: 0.14px;
  color: ${({ $active }) =>
    $active ? 'var(--Label-Strong, black)' : 'var(--Label-Neutral, rgba(46, 47, 51, 0.88))'};
  font-weight: ${({ $active }) => ($active ? 500 : 400)};
`;

const ArrowIcon = styled.svg<{ $disabled?: boolean; $right?: boolean }>`
  color: ${({ $disabled }) =>
    $disabled ? 'var(--Label-Disable, rgba(55, 56, 60, 0.16))' : 'var(--Label-Neutral, rgba(46, 47, 51, 0.88))'};
  flex-shrink: 0;
`;

const PageJumpContainer = styled.div`
  position: absolute;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    position: relative;
    right: auto;
    margin-top: 16px;
  }
`;

const PageJumpLabel = styled.span`
  text-align: center;
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 13px;
  font-family: 'Pretendard Variable', sans-serif;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0.25px;
`;

const PageJumpInputWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
`;

const PageJumpInputContainer = styled.div`
  align-self: stretch;
  justify-content: flex-start;
  align-items: center;
  display: inline-flex;
`;

const PageJumpInputContent = styled.div`
  flex: 1;
  justify-content: flex-start;
  align-items: center;
  display: flex;
`;

const PageJumpInput = styled.input`
  flex: 1;
  padding: 4px 6px;
  border-radius: 8px;
  border: 1px solid var(--Line-Normal-Neutral, rgba(112, 115, 124, 0.16));
  background: transparent;
  text-align: center;
  color: var(--Label-Normal, #171719);
  font-size: 14px;
  font-family: 'Pretendard Variable', sans-serif;
  font-weight: 500;
  line-height: 20.01px;
  letter-spacing: 0.20px;
  outline: none;

  &:focus {
    border-color: #007bff;
  }
`;

export default CommunityTablePagination;
